import {
    EditorClient,
    EditorServiceEndPoint,
    SessionType,
    SynonymServiceEndPoint,
} from '@nlapps/editor-client-web';
import { getAugloopServiceUrl } from 'augloop-service/lib/utils/getAugloopServiceUrl';
import { isFeatureEnabled } from 'owa-feature-flags';
import getUserLicenseType from 'owa-proofing-option/lib/utils/getUserLicenseType';
import { getIdentityProvider } from '../utils/getIdentityProvider';
import { getUserCulture, getUserConfiguration } from 'owa-session-store';
import { controlIconRegistration } from 'owa-control-icons';
import { PremiumPreviewStatus } from '../store/schema/PremiumPreviewData';
import { ISession } from '@augloop/runtime-client';
import { logUsage } from 'owa-analytics';
import { getFlightsToTrack } from 'owa-editor-augloop-plugin-utils/lib/getFlightsToTrack';

let editorBxClientData: { [editorProofingId: string]: EditorClient } = {};
const UPSELL_URL_TREATMENT2_GRAMMAR = 'https://go.microsoft.com/fwlink/?linkid=2125008';

export function getEditorBxClient(
    editorProofingId: string,
    userCulture: string | null,
    composeOperation: string | null,
    premiumPreviewStatus: PremiumPreviewStatus,
    augloopSession: ISession | null
): EditorClient {
    if (editorBxClientData[editorProofingId]) {
        return editorBxClientData[editorProofingId];
    }
    const userLocal: string = userCulture || getUserCulture();

    return initializeEditorBxClient(
        editorProofingId,
        userLocal,
        composeOperation,
        premiumPreviewStatus,
        augloopSession
    );
}

export function initializeEditorBxClient(
    editorProofingId: string,
    userCulture: string | null,
    composeOperation: string | null,
    premiumPreviewStatus: PremiumPreviewStatus,
    augloopSession: ISession | null
): EditorClient {
    const config = getUserConfiguration();
    const editorAlEnableFeature = 'mc-officeEditorALEnabled';
    const shouldEnableAl: boolean = isFeatureEnabled(editorAlEnableFeature);
    const flightsToTrack = getFlightsToTrack();

    const premiumPreviewEnabled: boolean =
        premiumPreviewStatus === PremiumPreviewStatus.New &&
        (isFeatureEnabled('mc-officeEditorUpsellSkittleBasedOnDate') ||
            isFeatureEnabled('mc-officeEditorUpsellSkittleBasedOnCount'));

    if (shouldEnableAl) {
        if (!augloopSession) {
            logUsage('ProofingALSessionNull');
        }
    }

    editorBxClientData[editorProofingId] = new EditorClient(
        {
            enableOrchestration: shouldEnableAl,
            orchestratorSettings: {
                enableAugLoop: shouldEnableAl,
                augLoopEndpoint: getAugloopServiceUrl(),
            },
            useShadowDom: false,
            appId: 'Outlook_Online',
            defaultLocale: userCulture,
            controlIconSetName: controlIconRegistration.fontFileName,
            showOptionsButton: false,
            showCalloutBranding: false,
            editorEndPoint: isFeatureEnabled('ring-dogfood')
                ? EditorServiceEndPoint.Staging
                : EditorServiceEndPoint.Prod,
            synonymsEndPoint: isFeatureEnabled('ring-dogfood')
                ? SynonymServiceEndPoint.Staging
                : SynonymServiceEndPoint.Prod,
            ignoreAllEnabled: true,
            ignoreOnceEnabled: true,
            sessionType:
                composeOperation === 'Edit'
                    ? SessionType.OWA_Draft_Email
                    : SessionType.OWA_New_Email,
            enableUrlClassifier: true,
            userId: {
                identityProvider: getIdentityProvider(),
                puid: config.SessionSettings?.UserPuid,
                tenantId: config.SessionSettings?.ExternalDirectoryTenantGuid,
            },
            showTeachingUi: false,
            showSquiggleTooltip: isFeatureEnabled('mc-officeEditorBXTooltip'),
            licenseType: getUserLicenseType(),
            upsellLink: UPSELL_URL_TREATMENT2_GRAMMAR,
            manageRtlInEditorUx: false,
            manageRtlForProofingLanguage: false,
            enableOptimizedCallingPattern: true,
            flightsToTrack: flightsToTrack,
            enableRoamingDictionary: true,
            enhancedUpsellEnabled: true,
            premiumPreviewEnabled: premiumPreviewEnabled,
            maxSuggestionDisplayPerLanguageNumber: 2,
            maxSuggestionItemsDisplayNumber: 3,
            newLineSentenceSeparatorEnabled: isFeatureEnabled(
                'mc-officeEditorNewSentenceSeparator'
            ),
            // Masking simplified host to non-augloop path only for the moment.
            simplifiedHostModelEnabled:
                !shouldEnableAl && isFeatureEnabled('mc-officeEditorSimplifiedHostModel'),
            enableE2eMetric: isFeatureEnabled('mc-officeEditorE2eMetric'),
            wordCountDetectorEnabled: isFeatureEnabled('mc-officeEditorWordCountDetector'),
            enableDispatchCritiquesOnEmptyDiffs: true,
            enableRenderingCalloutToPortal: true,
            inferenceCritiqueCloudIconEnabled: isFeatureEnabled(
                'mc-officeEditorInferenceCritiqueIcon'
            ),
        },
        augloopSession
    );

    return editorBxClientData[editorProofingId];
}

export function disposeEditorBxClient(editorProofingId: string): void {
    delete editorBxClientData[editorProofingId];
}
